import OrgForm from "@src/features/app/dialogs/orgManagement/OrgForm";
import {
  useUpdateCustomerFacingAccountPlanMutation,
  useUpdateOrgMutation,
} from "@src/features/app/dialogs/orgManagement/UpdateOrgDialog.generated";
import { useOrgFormState } from "@src/features/app/dialogs/orgManagement/useOrgFormState";
import { OrgFormStateFragment } from "@src/features/app/dialogs/orgManagement/useOrgFormState.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import gql from "graphql-tag";
import { DateTime } from "luxon";
import { FC } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";

type UpdateOrgDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: OrgFormStateFragment & { id: number };
};
const UpdateOrgDialog: FC<UpdateOrgDialogProps> = ({
  open,
  onOpenChange,
  org,
}) => {
  const state = useOrgFormState(org);
  const [updateOrg, { loading: updateOrgLoading }] = useUpdateOrgMutation();
  const [updateAccountPlan, { loading: updatePlanLoading }] =
    useUpdateCustomerFacingAccountPlanMutation();
  const loading = updateOrgLoading || updatePlanLoading;
  const { name, deactivatedAt, startedAt, seatsExpected, timezone, embedUrl } =
    state;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] w-[460px] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border p-4">
          <DialogTitle>Update Current Org</DialogTitle>
        </DialogHeader>
        <ScrollableFlexGrow flex={1} className="min-h-0" innerClassName="p-4">
          <OrgForm {...state} />
        </ScrollableFlexGrow>
        <DialogFooter className="shrink-0 border-t border-border p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={name === ""}
            loading={loading}
            onClick={async () => {
              const [orgResult, planResult] = await Promise.all([
                updateOrg({
                  variables: {
                    orgId: org.id,
                    input: {
                      name,
                      seatsExpected: seatsExpected || 0,
                      deactivatedAt: deactivatedAt
                        ? DateTime.fromJSDate(deactivatedAt).toISO()
                        : null,
                      startedAt: startedAt
                        ? DateTime.fromJSDate(startedAt).toISO()
                        : null,
                      timezone,
                    },
                  },
                }),
                updateAccountPlan({
                  variables: {
                    orgId: org.id,
                    embedUrl: embedUrl.trim() || null,
                  },
                }),
              ]);
              if (
                orgResult.data?.updateOrg.success &&
                planResult.data?.updateCustomerFacingAccountPlan.success
              ) {
                onOpenChange(false);
                launchConfetti();
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

gql`
  query OrgForUpdate {
    Me {
      user {
        org {
          id
          ...OrgFormState
        }
      }
    }
  }
  mutation UpdateOrg($orgId: Int!, $input: OrgInput!) {
    updateOrg(orgId: $orgId, input: $input) {
      success
      org {
        id
        name
        seatsExpected
        deactivatedAt
        startedAt
        timezone
      }
    }
  }
  mutation UpdateCustomerFacingAccountPlan($orgId: Int!, $embedUrl: String) {
    updateCustomerFacingAccountPlan(orgId: $orgId, embedUrl: $embedUrl) {
      success
      org {
        id
        customerFacingAccountPlan {
          id
          embedUrl
        }
      }
    }
  }
`;

export default UpdateOrgDialog;
